import React, { useState } from "react";
import { Sparkles, Loader2, BookOpen, Fingerprint, Check } from "lucide-react";
import { useTranslation } from "../../contexts/LanguageContext";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Input from "../ui/Input";
import apiService from "../../shared/apiService";
import { showToastError, showToastSuccess } from "../../shared/alertService";

interface ViralResearchModalProps {
  open: boolean;
  onClose: () => void;
  onUseContent: (content: string) => void;
}

const ViralResearchModal: React.FC<ViralResearchModalProps> = ({
  open,
  onClose,
  onUseContent,
}) => {
  const { t } = useTranslation();
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    content: string;
    sources_used: number;
    knowledge_context_applied: boolean;
  } | null>(null);

  const handleResearch = async () => {
    if (!topic.trim()) {
      showToastError(t("Por favor, insira um tema."));
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await apiService.post("/api/content/viral/research", {
        topic: topic.trim(),
      });

      if (response?.success && response.data) {
        setResult({
          content: response.data.generated_post,
          sources_used: response.data.sources_used,
          knowledge_context_applied: response.data.knowledge_context_applied,
        });
        showToastSuccess(t("Conteúdo viral gerado com sucesso!"));
      } else {
        showToastError(response?.error || t("Erro ao gerar conteúdo."));
      }
    } catch (error: any) {
      console.error("Erro no Deep Research:", error);
      showToastError(
        error.response?.data?.error || t("Erro ao conectar com a IA."),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUseContent = () => {
    if (result?.content) {
      onUseContent(result.content);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnOverlayClick
      title={t("Deep Research Viral")}
    >
      <div className="space-y-6">
        <p className="text-sm text-gray-500">
          {t(
            "Nossa IA vai ler o DNA da sua empresa e a Base de Conhecimento para criar um conteúdo altamente engajador.",
          )}
        </p>
        <div className="bg-linear-to-br from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-100">
          <Input
            label={t("Sobre o que você quer falar?")}
            placeholder={t(
              "Ex: Como a IA está revolucionando o atendimento ao cliente...",
            )}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            disabled={loading}
            className="w-full"
          />
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <div className="absolute inset-0 border-4 border-indigo-100 rounded-full animate-ping"></div>
              <Loader2 className="w-12 h-12 text-indigo-600 animate-spin relative z-10" />
            </div>
            <p className="text-sm font-medium text-indigo-600 animate-pulse">
              {t("Analisando Bases de Conhecimento e DNA da Empresa...")}
            </p>
          </div>
        )}

        {result && !loading && (
          <div className="space-y-4 animate-fade-in-up">
            <div className="flex gap-2 mb-4">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold border border-blue-200">
                <Fingerprint className="w-4 h-4" />
                {t("DNA Aplicado")}
              </div>
              {result.knowledge_context_applied && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold border border-emerald-200">
                  <BookOpen className="w-4 h-4" />
                  {t("{count} Fontes da Base Usadas", {
                    count: result.sources_used,
                  })}
                </div>
              )}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-[300px] overflow-y-auto whitespace-pre-wrap text-sm text-gray-800 shadow-inner">
              {result.content}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t("Cancelar")}
          </Button>

          {!result ? (
            <Button
              variant="primary"
              onClick={handleResearch}
              disabled={loading || !topic.trim()}
              icon={
                loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )
              }
              className="bg-linear-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              {t("Iniciar Deep Research")}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleResearch}
                disabled={loading}
                icon={<Sparkles className="w-4 h-4" />}
              >
                {t("Gerar Novamente")}
              </Button>
              <Button
                variant="primary"
                onClick={handleUseContent}
                icon={<Check className="w-4 h-4" />}
              >
                {t("Usar este Post")}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ViralResearchModal;
